import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Stack, Text, Badge, Inline, Heading, Box, Strong, Actions, Button, ButtonLink, Columns, Column, Hidden, IconChevron } from "braid-design-system";
import clsx from "clsx";
import { CarouselContext, ButtonBack, Slider, Slide, ButtonNext, DotGroup } from "pure-react-carousel";
import { useContext, useState, useEffect } from "react";
import { URL_PRODUCTS_AD_BUDGETS, URL_MANAGE_JOB } from "@seek/adv-constants";
import { useTranslations } from "@vocab/react";
import { HirerBalanceContext, useTracking, mapConsumableContract, PrepayAndSave } from "../../HirerBalancePresentation.mjs";
import { HirerBalanceLayout } from "../HirerBalanceLayout.mjs";
import { formatMinorUnits } from "@seek/currency";
import { ProgressCircle } from "../ProgressCircle/ProgressCircle.mjs";
import { translations } from "../../../../HirerContractBalance/components/Remaining/.vocab/index.mjs";
import { translations as translations$2 } from "../../../../HirerContractBalance/components/ContractBalance/.vocab/index.mjs";
import { translations as translations$1 } from "../../../../HirerContractBalance/components/Contract/.vocab/index.mjs";
import { translations as translations$3 } from "../../../../HirerContractBalance/components/SeekContractBalanceDescription/.vocab/index.mjs";
import { navButton, slider, navButtonNext, dotGroup } from "./BalanceCarousel.css.mjs";
const Remaining = (props) => {
  var _a, _b;
  const { t } = useTranslations(translations);
  const { isDollarValue, totalValue, warningType, onMouseOver, onMouseOut } = props;
  const currency = totalValue.dollars.remaining.currency;
  const hirerBalanceContext = useContext(HirerBalanceContext);
  const { locale } = hirerBalanceContext;
  const remainingValue = isDollarValue ? totalValue.dollars.remaining.value ?? 0 : ((_a = totalValue.units) == null ? void 0 : _a.remaining) ?? 0;
  const actualTotalValue = isDollarValue ? totalValue.dollars.original.value ?? 0 : ((_b = totalValue.units) == null ? void 0 : _b.original) ?? 0;
  const remainingPercentage = actualTotalValue > 0 ? remainingValue / actualTotalValue * 100 : 0;
  const isInCredit = remainingValue && remainingValue > 0;
  const minorUnits = formatMinorUnits(Math.floor(remainingValue), {
    currency,
    locale,
    dropFraction: true,
    notation: remainingValue.toString().length < 9 ? "standard" : "compact"
  });
  return isInCredit ? /* @__PURE__ */ jsx(
    ProgressCircle,
    {
      percentage: remainingPercentage,
      warningType,
      onMouseOver,
      onMouseOut,
      children: isDollarValue ? /* @__PURE__ */ jsxs(Stack, { space: "small", align: "center", children: [
        /* @__PURE__ */ jsxs(
          Text,
          {
            weight: "medium",
            size: minorUnits.toString().length > 9 ? "small" : "large",
            children: [
              " ",
              minorUnits
            ]
          }
        ),
        /* @__PURE__ */ jsx(Text, { size: "small", children: t("remaining") })
      ] }) : /* @__PURE__ */ jsxs(Text, { children: [
        remainingValue,
        " "
      ] })
    }
  ) : null;
};
const padToTwoDigits = (input) => `0${input}`.slice(-2);
const formatExpiryDate = (utcDateString) => {
  if (!utcDateString) {
    return "";
  }
  const utcDate = new Date(utcDateString);
  const date = padToTwoDigits(utcDate.getDate());
  const month = padToTwoDigits(utcDate.getMonth() + 1);
  return `${date}/${month}/${utcDate.getFullYear()}`;
};
const BadgeComponent = ({ daysLeft, warningType }) => {
  const { t } = useTranslations(translations$1);
  if (warningType === "ExpiresSoon") {
    return /* @__PURE__ */ jsx(Badge, { weight: "strong", tone: "caution", children: daysLeft === 1 ? t("Expires today") : t("Expires soon") });
  }
  if (warningType === "LowBalance") {
    return /* @__PURE__ */ jsx(Badge, { weight: "strong", tone: "caution", children: t("Low balance") });
  }
  return null;
};
const ContractBalanceHeader = ({
  contractBalance,
  currentContract,
  showHeading,
  warningType,
  showExpiryText
}) => {
  const { t } = useTranslations(translations$2);
  const { title, recommendUpSell } = contractBalance;
  const expiryDateUtc = currentContract == null ? void 0 : currentContract.expiry.expiryDate;
  const expiryText = expiryDateUtc ? t("Exp. date", { expiryDate: formatExpiryDate(expiryDateUtc) }) : null;
  const daysLeft = currentContract == null ? void 0 : currentContract.expiry.daysRemaining;
  return /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
    showHeading && /* @__PURE__ */ jsxs(Inline, { space: "small", alignY: "center", children: [
      /* @__PURE__ */ jsx(Heading, { level: "3", weight: "regular", children: t("Your SEEK Contract") }),
      warningType !== "NoWarning" && /* @__PURE__ */ jsx(BadgeComponent, { daysLeft, warningType })
    ] }),
    recommendUpSell ? null : /* @__PURE__ */ jsxs(Inline, { space: "small", alignY: "center", children: [
      /* @__PURE__ */ jsx(Text, { weight: "medium", children: title }),
      showExpiryText && expiryText !== null && /* @__PURE__ */ jsx(Badge, { tone: "neutral", children: expiryText })
    ] })
  ] });
};
const SubscriptionDetails = ({ contractBalance }) => {
  const { t } = useTranslations(translations$2);
  if (contractBalance.contractType !== "SUBSCRIPTION") {
    return null;
  }
  const {
    totalValue: { units },
    adTypeDescription = "Ads",
    contracts,
    dollarBased
  } = contractBalance;
  if (!contracts || contracts.length <= 0) {
    return null;
  }
  const daysLeft = contracts[0].expiry.daysRemaining;
  const { remaining: remainingUnits, original: originalUnits } = units ?? {};
  return /* @__PURE__ */ jsx(Box, { "data-testid": "Subscription", children: /* @__PURE__ */ jsxs(Text, { size: "small", children: [
    !dollarBased && (remainingUnits > 0 ? t("Ad type description remaining out of unit", {
      adTypeDescription,
      originalUnits: (originalUnits == null ? void 0 : originalUnits.toString()) || "",
      Strong: (v) => /* @__PURE__ */ jsx(Strong, { children: v })
    }) : t("You’ve spent your monthly ad allocation.")),
    daysLeft > 1 ? t("It will renew in n days.", {
      daysLeft: daysLeft.toString(),
      Strong: (v) => /* @__PURE__ */ jsx(Strong, { children: v })
    }) : t("It will renew ad midnight...", { daysLeft }),
    (remainingUnits ?? 0) <= 0 && t(" You will be charged at your contract rate if you post a new ad")
  ] }) });
};
const AdPacksAndSubsBalance = ({
  contractBalance,
  currentContract,
  showHeading
}) => {
  const { t } = useTranslations(translations$2);
  const hirerBalanceContext = useContext(HirerBalanceContext);
  const { showCallout, hideCallout } = hirerBalanceContext;
  const {
    dollarBased,
    contractType,
    recommendUpSell,
    totalValue,
    expiresSoon,
    lowBalance
  } = contractBalance;
  let warningType = "NoWarning";
  if (expiresSoon) {
    warningType = "ExpiresSoon";
  } else if (lowBalance) {
    warningType = "LowBalance";
  }
  const { onBalanceWidgetClicked } = useTracking();
  const { advertiserDetails } = hirerBalanceContext;
  const currentConsumableContract = mapConsumableContract({
    contractBalance,
    currentContract
  });
  const createJobAdClicked = () => {
    onBalanceWidgetClicked({
      ...advertiserDetails,
      actionOrigin: recommendUpSell ? "overage" : "in-credit",
      currentConsumableContract,
      objectInteraction: "create a job"
    });
    document.location.href = URL_MANAGE_JOB;
  };
  const buyAdBudgetClicked = () => {
    if (warningType === "ExpiresSoon") {
      onBalanceWidgetClicked({
        ...advertiserDetails,
        actionOrigin: "expiring-soon",
        currentConsumableContract,
        objectInteraction: "buy an adpack"
      });
    } else if (warningType === "LowBalance") {
      onBalanceWidgetClicked({
        ...advertiserDetails,
        actionOrigin: "low-balance",
        currentConsumableContract,
        objectInteraction: "buy an adpack"
      });
    }
  };
  return /* @__PURE__ */ jsx(HirerBalanceLayout, { children: /* @__PURE__ */ jsx(Box, { "data-testid": "AdPacksAndSubsBalance", children: /* @__PURE__ */ jsxs(Box, { display: "flex", width: "full", height: "full", position: "relative", children: [
    /* @__PURE__ */ jsx(Box, { paddingX: "medium", paddingTop: "large", children: /* @__PURE__ */ jsx(
      Remaining,
      {
        totalValue,
        isDollarValue: dollarBased,
        warningType,
        onMouseOver: () => showCallout(contractBalance, warningType),
        onMouseOut: () => hideCallout()
      }
    ) }),
    /* @__PURE__ */ jsx(Box, { width: "full", height: "full", paddingX: "small", paddingTop: "small", children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
      /* @__PURE__ */ jsx(
        ContractBalanceHeader,
        {
          contractBalance,
          currentContract,
          showHeading,
          warningType,
          showExpiryText: true
        }
      ),
      contractType === "SUBSCRIPTION" && /* @__PURE__ */ jsx(SubscriptionDetails, { contractBalance }),
      !recommendUpSell && /* @__PURE__ */ jsx(Actions, { children: /* @__PURE__ */ jsx(Button, { onClick: createJobAdClicked, children: t("Create a job ad") }) }),
      recommendUpSell && /* @__PURE__ */ jsx(Actions, { children: /* @__PURE__ */ jsx(
        ButtonLink,
        {
          href: URL_PRODUCTS_AD_BUDGETS,
          onClick: buyAdBudgetClicked,
          children: t("Buy a new Ad Budget")
        }
      ) })
    ] }) })
  ] }) }, "data") });
};
const SeekContractBalanceDescription = ({ contractBalance, currentContract }) => {
  const { t } = useTranslations(translations$3);
  if (contractBalance.recommendUpSell) {
    return /* @__PURE__ */ jsxs(Text, { children: [
      t("you've used your..."),
      " ",
      formatExpiryDate(currentContract.expiry.expiryDate)
    ] });
  }
  return null;
};
const SeekContractBalance = ({
  contractBalance,
  currentContract,
  showHeading
}) => {
  const { t } = useTranslations(translations$2);
  const { totalValue, dollarBased, recommendUpSell } = contractBalance;
  const hirerBalanceContext = useContext(HirerBalanceContext);
  const { showCallout, hideCallout } = hirerBalanceContext;
  const warningType = "NoWarning";
  const { onBalanceWidgetClicked } = useTracking();
  const { advertiserDetails } = hirerBalanceContext;
  const currentConsumableContract = mapConsumableContract({
    contractBalance,
    currentContract
  });
  const createJobAdClicked = () => {
    onBalanceWidgetClicked({
      ...advertiserDetails,
      actionOrigin: recommendUpSell ? "overage" : "in-credit",
      currentConsumableContract,
      objectInteraction: "create a job"
    });
    document.location.href = URL_MANAGE_JOB;
  };
  const buyAdBudgetClicked = () => {
    onBalanceWidgetClicked({
      ...advertiserDetails,
      actionOrigin: "overage",
      currentConsumableContract,
      objectInteraction: "buy a new ad budget"
    });
    document.location.href = URL_PRODUCTS_AD_BUDGETS;
  };
  return /* @__PURE__ */ jsx(HirerBalanceLayout, { upsell: recommendUpSell, children: /* @__PURE__ */ jsx(Box, { "data-testid": "SeekContractBalance", display: "flex", children: /* @__PURE__ */ jsx(Stack, { space: "gutter", children: /* @__PURE__ */ jsxs(Columns, { space: "gutter", collapseBelow: "desktop", children: [
    /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsxs(Stack, { space: "gutter", children: [
      recommendUpSell ? /* @__PURE__ */ jsx(Box, { paddingTop: { desktop: "medium" }, children: /* @__PURE__ */ jsx(PrepayAndSave, {}) }) : /* @__PURE__ */ jsx(
        Remaining,
        {
          totalValue,
          isDollarValue: dollarBased,
          warningType,
          onMouseOver: () => showCallout(contractBalance, warningType),
          onMouseOut: () => hideCallout()
        }
      ),
      /* @__PURE__ */ jsx(Hidden, { above: "tablet", children: /* @__PURE__ */ jsxs(Stack, { space: "gutter", children: [
        /* @__PURE__ */ jsx(
          ContractBalanceHeader,
          {
            contractBalance,
            currentContract,
            showHeading,
            warningType,
            showExpiryText: !recommendUpSell
          }
        ),
        recommendUpSell && /* @__PURE__ */ jsx(Box, { marginBottom: "medium", children: /* @__PURE__ */ jsx(
          SeekContractBalanceDescription,
          {
            contractBalance,
            currentContract
          }
        ) })
      ] }) })
    ] }) }),
    /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Stack, { space: "gutter", children: [
      /* @__PURE__ */ jsx(Hidden, { below: "desktop", children: /* @__PURE__ */ jsxs(Stack, { space: "gutter", children: [
        /* @__PURE__ */ jsx(
          ContractBalanceHeader,
          {
            contractBalance,
            currentContract,
            showHeading,
            warningType,
            showExpiryText: !recommendUpSell
          }
        ),
        recommendUpSell && /* @__PURE__ */ jsx(Box, { marginBottom: "medium", children: /* @__PURE__ */ jsx(
          SeekContractBalanceDescription,
          {
            contractBalance,
            currentContract
          }
        ) })
      ] }) }),
      /* @__PURE__ */ jsxs(Inline, { space: "small", alignY: "center", collapseBelow: "desktop", children: [
        /* @__PURE__ */ jsx(Button, { onClick: createJobAdClicked, children: t("Create a job ad") }),
        recommendUpSell && /* @__PURE__ */ jsx(Box, { textAlign: "center", children: /* @__PURE__ */ jsx(Button, { variant: "ghost", onClick: buyAdBudgetClicked, children: t("Buy a new Ad Budget") }) })
      ] })
    ] }) })
  ] }) }) }, "data") });
};
const ContractBalance = (contractBalanceProps) => {
  const {
    contractBalance,
    contractBalance: { contracts, contractType },
    showHeading = true
  } = contractBalanceProps;
  const currentContract = (contracts ?? []).filter((x) => x.current)[0];
  return /* @__PURE__ */ jsx(Stack, { space: "none", children: contractType === "SEEK_CONTRACT" ? /* @__PURE__ */ jsx(
    SeekContractBalance,
    {
      contractBalance,
      currentContract,
      showHeading
    }
  ) : /* @__PURE__ */ jsx(
    AdPacksAndSubsBalance,
    {
      contractBalance,
      currentContract,
      showHeading
    }
  ) });
};
const BalanceCarouselWrapper = ({ balances }) => {
  const carouselContext = useContext(CarouselContext);
  const [currentSlide, setCurrentSlide] = useState(
    carouselContext.state.currentSlide
  );
  useEffect(() => {
    const onChange = () => {
      setCurrentSlide(carouselContext.state.currentSlide);
    };
    carouselContext.subscribe(onChange);
    return () => carouselContext.unsubscribe(onChange);
  }, [carouselContext]);
  const balanceCount = balances.length;
  const showNextButton = balanceCount > 1 && currentSlide < balanceCount - 1;
  const showPrevButton = currentSlide > 0;
  const hirerBalanceContext = useContext(HirerBalanceContext);
  const { onBalanceWidgetClicked } = useTracking();
  const { advertiserDetails } = hirerBalanceContext;
  const buttonBackClicked = () => {
    const contractBalance = balances[currentSlide];
    const currentContract = (contractBalance.contracts ?? []).filter(
      (x) => x.current
    )[0];
    const currentConsumableContract = mapConsumableContract({
      contractBalance,
      currentContract
    });
    onBalanceWidgetClicked({
      ...advertiserDetails,
      currentConsumableContract,
      objectInteraction: "carousel left"
    });
  };
  const buttonNextClicked = () => {
    const contractBalance = balances[currentSlide];
    const currentContract = (contractBalance.contracts ?? []).filter(
      (x) => x.current
    )[0];
    const currentConsumableContract = mapConsumableContract({
      contractBalance,
      currentContract
    });
    onBalanceWidgetClicked({
      ...advertiserDetails,
      currentConsumableContract,
      objectInteraction: "carousel right"
    });
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    showPrevButton && /* @__PURE__ */ jsx(
      ButtonBack,
      {
        className: navButton,
        onClick: buttonBackClicked,
        "data-testid": "button-back",
        children: /* @__PURE__ */ jsx(
          IconChevron,
          {
            title: "Previous",
            titleId: "Previous",
            direction: "left",
            tone: "link",
            size: "xsmall"
          }
        )
      }
    ),
    /* @__PURE__ */ jsx(Slider, { className: slider, children: balances.map((contractBalance, index) => /* @__PURE__ */ jsx(Slide, { index, children: /* @__PURE__ */ jsx(Box, { "data-testid": "BalanceRow", height: "full", children: /* @__PURE__ */ jsx(
      ContractBalance,
      {
        contractBalance,
        showHeading: true
      }
    ) }, "data") }, index)) }),
    showNextButton && /* @__PURE__ */ jsx(
      ButtonNext,
      {
        className: clsx(navButton, navButtonNext),
        onClick: buttonNextClicked,
        "data-testid": "button-next",
        children: /* @__PURE__ */ jsx(
          IconChevron,
          {
            title: "Next",
            titleId: "Next",
            direction: "right",
            tone: "link",
            size: "xsmall"
          }
        )
      }
    ),
    balanceCount > 1 && /* @__PURE__ */ jsx(DotGroup, { className: dotGroup })
  ] });
};
export {
  BadgeComponent,
  BalanceCarouselWrapper,
  Remaining,
  formatExpiryDate
};
